/**
 * Featured — what Priori staff published for this club from the Insights
 * Lab: single promoted insights and composed narratives, above the match
 * stories on /insights.
 *
 * FAILS TO NOTHING, deliberately. If /api/v1/insights/promoted is absent
 * (backend not deployed yet) or errors, the section renders nothing rather
 * than an error — the rest of the Insights page is unaffected.
 *
 * A frozen-HTML promotion renders in a fully sandboxed script-less iframe
 * (sandbox="", opaque origin), CSS-scaled from its captured pixel size; the
 * PNG is the fallback face everywhere else.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import { API_BASE } from '../lib/api'
import { withViewAs } from '../lib/viewAs'
import { getPromoted, type PromotedFeed, type PromotedItem } from '../packs/promotions'

import './insights.css'

type Slide = {
  headline: string
  caption: string | null
  game_code: string
  image_url: string
  html: { url: string; width: number; height: number } | null
}

type Overlay = { title: string; meta: string; slides: Slide[] }

export default function InsightsFeatured() {
  const [feed, setFeed] = useState<PromotedFeed | null>(null)
  const [overlay, setOverlay] = useState<Overlay | null>(null)

  useEffect(() => {
    getPromoted()
      .then(setFeed)
      .catch(() => setFeed(null)) // absent or refused -> no section at all
  }, [])

  if (!feed) return null
  const { promotions, narratives } = feed
  if (promotions.length === 0 && narratives.length === 0) return null

  const openPromotion = (p: PromotedItem) =>
    setOverlay({
      title: p.headline,
      meta: `Featured insight · ${p.game_code}`,
      slides: [p],
    })

  return (
    <>
      <h2 className="ftd-label">Featured</h2>
      <div className="ftd-grid">
        {narratives.map((n) => (
          <button className="ftd-card" key={`n${n.id}`} onClick={() =>
            setOverlay({
              title: n.title,
              meta: [n.scope_label, `${n.items.length} insights`]
                .filter(Boolean).join(' · '),
              slides: n.items,
            })}>
            <span className="ftd-thumb">
              {n.items[0] && <img src={n.items[0].image_url} alt="" loading="lazy" />}
              <span className="ftd-count">{n.items.length} insights</span>
            </span>
            <span className="ftd-foot">
              <span className="ftd-title">{n.title}</span>
              <span className="ftd-meta">
                {[n.scope_label, n.intro].filter(Boolean).join(' · ')
                  || `${n.items.length} insights`}
              </span>
            </span>
          </button>
        ))}
        {promotions.map((p) => (
          <button className="ftd-card" key={`p${p.id}`} onClick={() => openPromotion(p)}>
            <span className="ftd-thumb">
              <img src={p.image_url} alt="" loading="lazy" />
            </span>
            <span className="ftd-foot">
              <span className="ftd-title">{p.headline}</span>
              <span className="ftd-meta">
                {[p.game_code, p.caption].filter(Boolean).join(' · ')}
              </span>
            </span>
          </button>
        ))}
      </div>
      {overlay && <FeaturedViewer overlay={overlay} onClose={() => setOverlay(null)} />}
    </>
  )
}

function FeaturedViewer({ overlay, onClose }: { overlay: Overlay; onClose: () => void }) {
  const [i, setI] = useState(0)
  const dialog = useRef<HTMLDivElement>(null)
  const count = overlay.slides.length
  const next = useCallback(() => setI((n) => Math.min(n + 1, count - 1)), [count])
  const prev = useCallback(() => setI((n) => Math.max(n - 1, 0)), [])

  useEffect(() => {
    dialog.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowRight') next()
      else if (e.key === 'ArrowLeft') prev()
    }
    window.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [next, prev, onClose])

  const slide = overlay.slides[i]

  return (
    <div className="ins-scrim" onClick={onClose}>
      <div
        className="ins-stage"
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-label={overlay.title}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        {count > 1 && (
          <div className="ins-bars">
            {overlay.slides.map((_, n) => (
              <span key={n}
                    className={`ins-bar${n < i ? ' is-done' : n === i ? ' is-current' : ''}`} />
            ))}
          </div>
        )}
        <div className="ins-top">
          <div>
            <div className="ins-match">{overlay.title}</div>
            <div className="ins-meta">
              {overlay.meta}{count > 1 ? ` · ${i + 1} of ${count}` : ''}
            </div>
          </div>
          <button className="ins-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        {slide && (
          <div className="ins-slide">
            <div className="ins-kicker">{slide.game_code}</div>
            <h3>{slide.headline}</h3>
            <div className="ins-viz">
              {slide.html
                // the browser loads the frame itself: it carries this
                // tab's grant, or a club's tile is answered as the staff
                // member (404, or another club's page)
                ? <ScaledHtml url={withViewAs(slide.html.url, API_BASE)} width={slide.html.width}
                              height={slide.html.height} />
                : <img src={slide.image_url} alt={slide.headline} />}
            </div>
            {slide.caption && <p className="ins-read">{slide.caption}</p>}
          </div>
        )}
        {count > 1 && (
          <>
            <div className="ftd-tap ftd-tap--left" onClick={prev} />
            <div className="ftd-tap ftd-tap--right" onClick={next} />
          </>
        )}
      </div>
    </div>
  )
}

/**
 * A frozen-HTML promotion slide: the document was captured at a fixed pixel
 * width (every style is inlined in px), so it cannot reflow — the iframe
 * renders at its captured size and is CSS-scaled to fit. sandbox="" (no
 * tokens): no scripts, opaque origin; the serving route's CSP says the same
 * thing server-side.
 */
export function ScaledHtml({ url, width, height }: {
  url: string; width: number; height: number
}) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)
  useLayoutEffect(() => {
    const el = wrapRef.current
    if (!el) return
    const fit = () => {
      const maxH = el.parentElement?.clientHeight || Infinity
      setScale(Math.min(1, el.clientWidth / width || 1, maxH / height))
    }
    fit()
    const ro = new ResizeObserver(fit)
    ro.observe(el)
    if (el.parentElement) {
      ro.observe(el.parentElement)
    }
    return () => ro.disconnect()
  }, [width, height])
  return (
    <div ref={wrapRef} className="ins-html" style={{ height: height * scale }}>
      <iframe
        src={url}
        title="Insight"
        sandbox=""
        width={width}
        height={height}
        style={{ border: 0, transform: `scale(${scale})`, transformOrigin: 'top left' }}
      />
    </div>
  )
}
